import React, { useState, useEffect } from "react";
import ApplicantForm from "./ApplicantForm";
import ContractorForm from "./Contractor";
import LocationForm from "./Location";
import ConsentForm from "./Consent";
import Container from "react-bootstrap/Container";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import UserTermsModal from "../Modals/UserTermsModal";
import ThankYouSuccessModal from "../Modals/ThankYouSuccessModal";
import { validateField } from "../../utils/validation/validation";
import { useNavigate } from "react-router-dom";
import { submitApplication } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";

const ApplicationForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [thankYouModalShow, setThankyouModalShow] = useState(false);
  const [addOrUpdateButtonText, setAddOrUpdateButtonText] =
    useState("Add Contractor");
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);
  const [errors, setErrors] = useState({
    applicant: {},
    contractors: [],
    location: {},
    community: null,
  });
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const role = localStorage.getItem('selectedRole');

  // Redirect to home if role is not selected (direct URL access)
  useEffect(() => {
    if (!role) {
      navigate("/");
    }
  }, [role, navigate]);

  const initialContractorFields = {
    individualName: "",
    trade: "",
    email: "",
    phoneNumber: "",
    address: "",
    license: "",
  };
  const initialFormValue = {
    applicant: {
      firstName: "",
      lastName: "",
      address: "",
      email: "",
      phoneNumber: "",
      communityEmail: "",
      notes: "",
      role: ""
    },
    contractors: [],
    location: {
      address: "",
      permitType: "Residential",
      description: "",
      cost: 0,
      locationFiles: "",
    }
  };
  const [selectedContractorIndex, setSelectedContractorIndex] = useState(null);
  const [formData, setFormData] = useState(initialFormValue);
  const [currentContractor, setCurrentContractor] = useState(
    initialContractorFields
  );

  const handleDataChange = (section, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: { ...prevData[section], [field]: value },
    }));
  };

  const validateContractorFields = () => {
    const errors = {};
    const requiredFields = [
      "individualName",
      "trade",
      "email",
      "phoneNumber",
      "address"
    ];

    requiredFields.forEach((field) => {
      const error = validateField(field, currentContractor[field]);
      if (error) {
        errors[field] = error;
      }
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      currentContractor: errors,
    }));

    return Object.keys(errors).length === 0;
  };

  const handleAddOrUpdateContractor = (callback = () => {}) => {
    if (validateContractorFields()) {
        setFormData((prevData) => {
            const updatedContractors = [...prevData.contractors];
            if (selectedContractorIndex !== null) {
                updatedContractors[selectedContractorIndex] = currentContractor;
            } else {
                updatedContractors.push(currentContractor);
            }

            return { ...prevData, contractors: updatedContractors };
        });

        setCurrentContractor(initialContractorFields);
        setSelectedContractorIndex(null);
        setAddOrUpdateButtonText("Add Contractor");
        setIsNextButtonDisabled(false); 

        setTimeout(callback, 0);
    }
};

const addContractorAndNext = () => {
  setIsNextButtonDisabled(true);
  
  handleAddOrUpdateContractor(() => {
      setFormData((prevData) => {
          const updatedFormData = { ...prevData };
          
          // Ensure the latest state is used
          if (validateContractorFields() && updatedFormData.contractors.length > 0) {
              setCurrentStep((prevStep) => prevStep + 1);
          }

          return updatedFormData;
      });
  });
};


  const handleSelectContractor = (index) => {
    setCurrentContractor(formData.contractors[index]);
    setSelectedContractorIndex(index);
    setAddOrUpdateButtonText("Update Contractor");
    setIsNextButtonDisabled(true);
  };

  const handleValidate = (section, field, error) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [section]: { ...prevErrors[section], [field]: error },
    }));
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1: {
        const { firstName, lastName, email, address, phoneNumber, communityEmail } =
          formData.applicant;
        return (
          firstName &&
          lastName &&
          address &&
          email &&
          phoneNumber &&
          communityEmail &&
          Object.values(errors.applicant).every((value) => value === "")
        );
      }
      case 2: {
        return !isNextButtonDisabled && formData.contractors.length > 0;
      }
      case 3: {
        const { address, cost } = formData.location;
        return (
          address &&
          cost &&
          Object.values(errors.location).every((value) => value === "")
        );
      }
      case 4: {
        return true;
      }
      default:
        return false;
    }
  };

  const handleSubmit = () => {
    if (isStepValid()) {
      setModalShow(true);
    } else {
      showToast("Please fill in all required fields before submitting.", "error");
    }
  };

  const submitForm = async () => {
    setIsSubmitting(true);
    startLoading();
    try {
      formData.applicant.role = role;
      await submitApplication(formData);
      setModalShow(false);
      setThankyouModalShow(true);
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("There was an error submitting your application.", "error");
      }
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };

  const handleConsentYes = () => {
    setModalShow(false);
    submitForm();
  };

  const resetForm = () => {
    setFormData(initialFormValue);
    setCurrentStep(1);
    navigate("/");
  };

  const handleRedirectModel = () => {
    setThankyouModalShow(false);
    resetForm();
  }

  const handleConsentNo = () => {
    setModalShow(false);
    resetForm();
  };

  const nextStep = () => {
    if (isStepValid()) {
      setCurrentStep((prevStep) => prevStep + 1);
    } else {
      showToast("Please fill in all required fields before proceeding.", "error");
    }
  };

  const prevStep = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ApplicantForm
            data={formData.applicant}
            onChange={(field, value) =>
              handleDataChange("applicant", field, value)
            }
            onValidate={(field, error) =>
              handleValidate("applicant", field, error)
            }
            errors={errors.applicant || {}}
          />
        );
      case 2:
        return (
          <ContractorForm
            data={currentContractor}
            contractors={formData.contractors}
            onChange={(field, value) =>
              setCurrentContractor({ ...currentContractor, [field]: value })
            }
            onValidate={(field, error) =>
              handleValidate("currentContractor", field, error)
            }
            onAddOrUpdateContractor={handleAddOrUpdateContractor}
            onSelectContractor={handleSelectContractor}
            errors={errors.currentContractor || {}}
            buttonText={addOrUpdateButtonText}
          />
        );
      case 3:
        return (
          <LocationForm
            data={formData.location}
            onChange={(field, value) =>
              handleDataChange("location", field, value)
            }
            onValidate={(field, error) =>
              handleValidate("location", field, error)
            }
            errors={errors.location || {}}
          />
        );
      case 4:
        return (
          <ConsentForm
            data={formData.consent}
            onChange={(field, value) =>
              handleDataChange("consent", field, value)
            }
          />
        );
      default:
        return <ApplicantForm />;
    }
  };

  return (
    <>
      <Container className="stepper-container px-5" fluid>
        <div className="stepper">
          <div className={`step ${currentStep === 1 ? "active" : ""} step1`}>
            {currentStep > 1 ? (
              <img src={`${process.env.PUBLIC_URL}/Step-Success.svg`} className="px-2" alt="" />
            ) : (
              <img src={`${process.env.PUBLIC_URL}/application-form.svg`} className="px-2" alt="" />
            )}
            <div>Permit Applicant</div>
          </div>
          <div className={`step ${currentStep === 2 ? "active" : ""} step2`}>
            {currentStep > 2 ? (
              <img src={`${process.env.PUBLIC_URL}/Step-Success.svg`} className="px-2" alt="" />
            ) : (
              <img src={`${process.env.PUBLIC_URL}/contract-from.svg`} className="px-2" alt="" />
            )}
            <div>Contractor</div>
          </div>
          <div className={`step ${currentStep === 3 ? "active" : ""} step3`}>
            {currentStep > 3 ? (
              <img src={`${process.env.PUBLIC_URL}/Step-Success.svg`} className="px-2" alt="" />
            ) : (
              <img src={`${process.env.PUBLIC_URL}/location-form.svg`} className="px-2" alt="" />
            )}
            <div>Proposed Work Location</div>
          </div>
          <div className={`step ${currentStep === 4 ? "active" : ""} step4`}>
            {currentStep > 4 ? (
              <img src={`${process.env.PUBLIC_URL}/Step-Success.svg`} className="px-2" alt="" />
            ) : (
              <img src={`${process.env.PUBLIC_URL}/consent-form.svg`} className="px-2" alt="" />
            )}
            <div>User Terms</div>
          </div>
        </div>

        {/* Step Content */}
        {renderStep()}

        {/* Navigation Buttons */}
        <div className="stepper-navigation d-flex mb-4 justify-content-end mt-4 ">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="btn btn-previous mx-4"
            >
              <span>
                <FontAwesomeIcon icon={faArrowLeft} />
              </span>{" "}
              Previous
            </button>
          )}
          {currentStep < 4 && currentStep === 2 &&
            (<button
              type="button"
              onClick={addContractorAndNext}
              className="btn btn-next"
            >
              Next{" "}
              <span>
                <FontAwesomeIcon icon={faArrowRight} />
              </span>
            </button>
            )}
          {currentStep < 4 && currentStep !== 2 && (

            <button
              type="button"
              onClick={nextStep}
              className="btn btn-next"
              disabled={!isStepValid() || isNextButtonDisabled}
            >
              Next{" "}
              <span>
                <FontAwesomeIcon icon={faArrowRight} />
              </span>
            </button>
          )}
          {currentStep === 4 && (
            <button
              type="submit"
              id="submitForm"
              onClick={handleSubmit}
              className="btn btn-submit"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          )}
        </div>
      </Container>
      <UserTermsModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        onConsentYes={handleConsentYes}
        onConsentNo={handleConsentNo}
      />
      <ThankYouSuccessModal
        show={thankYouModalShow}
        onHide={handleRedirectModel}
        onRedirect={resetForm}
      />
    </>
  );
};

export default ApplicationForm;